import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, IeAnexo, ToastServiceStipulator } from '@philips-emr/hpms-stipulator-commons';
import StipulatorContext from '@philips-emr/hpms-stipulator-commons/lib/contexts/stipulator-context';
import { DeckAPI, FetchAPI, PanelAPI, Stamp, StampIcon, TextAPI } from '@philips-emr/tws-components';
import { Color } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnBeforeFetch, OnChange, OnDynamicContentRender, OnMount, OnSelectItem } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent, RenderEvent, SelectItemEvent } from '@philips-emr/tws-core/lib/component/types';
import { FileService, MessageService } from '@philips-emr/tws-core/lib/services';
import { HttpMethod } from '@philips-emr/tws-core/lib/services/http-service';
import { applicationContext } from '@philips-emr/tws-foundation';

import { FragmentNames } from '../codegen';
import { BeneficiarioDossieAliases as Aliases } from '../codegen/constants/aliases/beneficiario-dossie-aliases';

import BaseController from './BaseController';

interface Dados {
  nrSeqSegurado: number,
  dsStatus: string,
  ieCumprida: CarenciaStatusSuperscription,
}

enum CarenciaStatusSuperscription {
  SIM = 'S',
  NAO = 'N',
}

interface Documento {
  nrSeqDoc: number,
  dsArquivo: string,
}

@Controller({ fragment: FragmentNames.BENEFICIARIO_DOSSIE })
class BeneficiaryChangeController extends BaseController {

  @Inject(Aliases.PANEL_DADOS_BENEF)
  private panelControl: PanelAPI;

  @Inject(Aliases.PANEL_MSG_DEFAULT_BENEFICIARIO)
  private panelMsgDefaultBeneficiario: PanelAPI;

  @Inject(Aliases.PANEL_SERVICOS)
  private panelServicos: PanelAPI;

  @Inject(Aliases.TEXT_MENSAGEM_CARENCIA)
  private textMensagemCarencia: TextAPI;

  @OnMount(Aliases.PANEL_BENEFICIARIO_DOSSIE)
  onMountBeneficiario(): void {
    this.context.set({ isDossie: true });
    applicationContext.openModule('dossie-beneficiario');
  }

  @OnChange(Aliases.FIELD_BENEFICIARIO)
  onChangBeneficiario(e: ChangeEvent<string>): void {
    const { nrSeqSegurado: nrSeqBeneficiario } = e.value as unknown as Dados;
    this.context.set({ nrSeqBeneficiario });

    this.atualizaDados(!!nrSeqBeneficiario);
    this.panelMsgDefaultBeneficiario.setVisible(!nrSeqBeneficiario);
  }

  @OnAfterFetch(Aliases.FETCH_SERVICOS)
  onAfterServicos(event: FetchEvent): void {
    if (!event.data) {
      return;
    }
    if (Array.isArray(event.data) && event.data.length === 0) {
      this.panelServicos.setVisible(false);
    }
  }

  @OnDynamicContentRender(Aliases.HPMS_CONSULTA_BENEFICIARIO)
  onDynamicContentRenderCardLote(event: RenderEvent): void {
    const { data, render } = event;
    const { ieCumprida, dsStatus } = data as Dados;

    const content = (): ReactElement => (this.getCarenciaStamp(ieCumprida, dsStatus));

    render(content);
  }

  @OnMount(Aliases.PANEL_DADOS_BENEF)
  onRenderPanelDados(): void {
    const nrSeqBeneficiario = this.context.get('nrSeqBeneficiario') as number;
    let isVisiblePanelCtrl = false;
    let isVisiblePanelMsgBeneficairio = false;

    if (nrSeqBeneficiario && !this.panelControl.isVisible()) {
      isVisiblePanelCtrl = true;
      isVisiblePanelMsgBeneficairio = false;
    } else {
      isVisiblePanelCtrl = false;
      isVisiblePanelMsgBeneficairio = true;
    }

    setTimeout(() => {
      this.panelControl.setVisible(isVisiblePanelCtrl);
      this.panelMsgDefaultBeneficiario.setVisible(isVisiblePanelMsgBeneficairio);
    });
  }

  @OnSelectItem(Aliases.DECK_DOSSIE)
  onClickDownloadDocumento(event: SelectItemEvent<any>): void {
    const { nrSeqDoc, dsArquivo } = event.item as Documento;
    const api = event.target as DeckAPI;
    api.unselectAll();
    this.downloadDocumento(nrSeqDoc, dsArquivo);
  }

  @OnSelectItem(Aliases.DECK_RESPONSIVO)
  onClickDownloadDocumentoResponsivo(event: SelectItemEvent<any>): void {
    const { nrSeqDoc, dsArquivo } = event.item as Documento;
    const api = event.target as DeckAPI;
    api.unselectAll();
    this.downloadDocumento(nrSeqDoc, dsArquivo);
  }

  @OnAfterFetch(Aliases.FETCH_MENSAGEM_CARENCIA)
  OnAfterFetch(event: FetchEvent): void {
    if (!event.data) {
      return;
    }
    const mensagemCarencia = event.data as unknown as string;
    if (mensagemCarencia) {
      this.textMensagemCarencia.setValue(mensagemCarencia);
    }
  }

  @OnBeforeFetch(Aliases.FETCH_DOSSIE_CONTRATO_DOCUMENTOS)
  onBeforeFetchDossieContratoDocumentos(event: MountEvent): void {
    const fetch = event.target as unknown as FetchAPI;
    const { nrSeqSubestipulante } = StipulatorContext.getCurrentContract();
    nrSeqSubestipulante && fetch.setParameters({ ...fetch.getParameters(), nrSeqSubestipulante });
  }

  @OnBeforeFetch(Aliases.FETCH_CONSULTA_BENEFICIARIO)
  onBeforeFetchConsultaBeneficiario(event: MountEvent): void {
    const fetch = event.target as unknown as FetchAPI;
    const { nrSeqSubestipulante } = StipulatorContext.getCurrentContract();
    nrSeqSubestipulante && fetch.setParameters({ ...fetch.getParameters(), nrSeqSubestipulante });
  }

  @OnBeforeFetch(Aliases.FETCH_DOSSIE)
  onBeforeFetchDossie(event: MountEvent): void {
    const fetch = event.target as unknown as FetchAPI;
    const { nrSeqSubestipulante } = StipulatorContext.getCurrentContract();
    nrSeqSubestipulante && fetch.setParameters({ ...fetch.getParameters(), nrSeqSubestipulante });
  }

  private downloadDocumento = (nrSeqDoc: number, dsArquivo: string): void => {

    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();
    const ieTabela = IeAnexo.DOSSIE_BENEFICIARIO_DOCUMENTO;

    ToastServiceStipulator.information(MessageService.translate('beneficiario.download-iniciado'));
    FileService.download(Endpoint.GET.DOWNLOAD_DOCUMENTOS, dsArquivo, { ieTabela, nrSeqDoc, nrSeqContrato }, HttpMethod.GET)
      .catch(() => ToastServiceStipulator.error(MessageService.translate('stipulator.arquivo-nao-localizado')));

  };

  private atualizaDados(visible: boolean): void {
    if (this.panelControl.isVisible()) {
      this.panelServicos.setVisible(visible);
    }

    this.panelControl.setVisible(visible);
  }

  private getCarenciaStamp(ieCumprida: CarenciaStatusSuperscription, dsStatus: string): ReactElement {

    return (
      <Stamp
        color={this.getCarenciaIconColor(ieCumprida)}
        icon={this.getMensalidadeIconType(ieCumprida)}
        label={dsStatus}
      />
    );
  }

  private getCarenciaIconColor(situacaoSuperscription: CarenciaStatusSuperscription): Color {
    const color = {
      [CarenciaStatusSuperscription.SIM]: Color.SAFETY_GREEN,
      [CarenciaStatusSuperscription.NAO]: Color.SAFETY_NEUTRAL,
    };

    return color[situacaoSuperscription];
  }

  private getMensalidadeIconType(situacaoSuperscription: CarenciaStatusSuperscription): StampIcon {
    const icon = {
      [CarenciaStatusSuperscription.SIM]: StampIcon.CHECK,
      [CarenciaStatusSuperscription.NAO]: StampIcon.CLOCK,
    };

    return icon[situacaoSuperscription];
  }
}

export default BeneficiaryChangeController;
